/**
 * Rank Spotter - Main Application Component
 * Manages state and API communication
 */
import React, { useState } from 'react';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';
import SearchForm from './components/SearchForm';
import ResultsTable from './components/ResultsTable';

// Get API URL from environment or use default
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function App() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  /**
   * Handle SERP check request
   * @param {Object} data - { keywords, keyword, domain, recaptchaToken }
   */
  const handleSearch = async (data) => {
    setLoading(true);
    setResult(null);

    try {
      // Make API request
      const response = await axios.post(`${API_URL}/check-serp`, data, {
        headers: {
          'Content-Type': 'application/json'
        },
        timeout: 120000 // 120 seconds timeout (pagination can take 30-90 seconds for 100 results)
      });

      // Check if request was successful
      if (response.data.success) {
        const responseData = response.data.data;
        const totalKeywords = response.data.total_keywords;
        
        // Transform response for multiple keywords
        let resultData;
        if (totalKeywords && totalKeywords > 1) {
          // Multiple keywords - data is an array
          resultData = {
            domain: data.domain,
            timestamp: response.data.timestamp,
            total_keywords: totalKeywords,
            results: responseData // Array of results
          };
        } else {
          // Single keyword - data is an object
          resultData = responseData;
        }
        
        setResult(resultData);
        
        // Show success notification
        if (totalKeywords && totalKeywords > 1) {
          // Multiple keywords result
          const foundCount = responseData.filter(r => r.found).length;
          toast.success(
            `Checked ${totalKeywords} keywords. Found ${foundCount} ranking${foundCount !== 1 ? 's' : ''} for ${data.domain}`,
            { duration: 5000 }
          );
        } else if (resultData.found) {
          // Single keyword found
          toast.success(
            `Found! ${data.domain} ranks at position #${resultData.position}`,
            { duration: 4000 }
          );
        } else {
          // Single keyword not found
          toast.error(
            `${data.domain} was not found in top 50 results for "${resultData.keyword}"`,
            { duration: 5000 }
          );
        }
      } else {
        throw new Error(response.data.error || 'Unknown error occurred');
      }
    } catch (error) {
      console.error('Search error:', error);
      
      // Handle different error types
      let errorMessage = 'Failed to check SERP ranking. Please try again.';
      
      if (error.code === 'ECONNABORTED') {
        errorMessage = 'Request timed out. Please try again.';
      } else if (error.response) {
        // Server responded with error
        errorMessage = error.response.data?.error || errorMessage;
      } else if (error.request) {
        // Request made but no response
        errorMessage = 'Cannot connect to server. Please check if the backend is running.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-8 px-4 sm:px-6 lg:px-8">
      {/* Animated Background Pattern */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none opacity-20">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-300 rounded-full filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-300 rounded-full filter blur-3xl animate-pulse delay-1000"></div>
      </div>

      {/* Toast Notifications Container */}
      <Toaster 
        position="top-right"
        toastOptions={{
          success: {
            style: {
              background: '#10b981',
              color: '#fff',
              fontWeight: '600',
            },
            iconTheme: {
              primary: '#fff',
              secondary: '#10b981',
            },
          },
          error: {
            style: {
              background: '#ef4444',
              color: '#fff',
              fontWeight: '600',
            },
            iconTheme: {
              primary: '#fff',
              secondary: '#ef4444',
            },
          },
        }}
      />

      {/* Main Container */}
      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <header className="text-center mb-10">
          <div className="flex items-center justify-center mb-4">
            <div className="relative">
              <div className="absolute inset-0 bg-primary-600 rounded-full blur-xl opacity-50 animate-pulse"></div>
              <div className="relative bg-gradient-to-br from-primary-600 to-indigo-600 rounded-full p-4 shadow-2xl">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
          </div>
          <h1 className="text-5xl font-extrabold mb-3">
            <span className="bg-gradient-to-r from-primary-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
              Rank Spotter
            </span>
          </h1>
          <p className="text-xl text-gray-700 font-medium mb-4">
            Track Your Google Rankings in Real-Time
          </p>
          <div className="flex items-center justify-center gap-6 text-sm text-gray-600">
            <div className="flex items-center gap-1">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-semibold">Real-Time Data</span>
            </div>
            <div className="flex items-center gap-1">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-semibold">Top 50 Results</span>
            </div>
            <div className="flex items-center gap-1">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-semibold">4 Keywords at Once</span>
            </div>
          </div>
        </header>

        {/* Search Form */}
        <div className="mb-8">
          <SearchForm onSearch={handleSearch} loading={loading} />
        </div>

        {/* Results */}
        {result && <ResultsTable result={result} />}

        {/* Footer */}
        <footer className="mt-12 text-center">
          <div className="card max-w-3xl mx-auto bg-gradient-to-br from-white to-blue-50 border-2 border-primary-100 shadow-xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center justify-center gap-2">
              <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              How It Works
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-gray-600">
              <div className="text-center group hover:transform hover:scale-105 transition-transform duration-200">
                <div className="bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl w-16 h-16 flex items-center justify-center mx-auto mb-3 shadow-lg group-hover:shadow-xl transition-shadow">
                  <span className="text-white font-bold text-2xl">1</span>
                </div>
                <p className="font-semibold text-gray-900 mb-1 text-base">Enter Domain</p>
                <p className="text-sm">Input your website URL</p>
              </div>
              <div className="text-center group hover:transform hover:scale-105 transition-transform duration-200">
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl w-16 h-16 flex items-center justify-center mx-auto mb-3 shadow-lg group-hover:shadow-xl transition-shadow">
                  <span className="text-white font-bold text-2xl">2</span>
                </div>
                <p className="font-semibold text-gray-900 mb-1 text-base">Add Keywords</p>
                <p className="text-sm">Enter 1-4 search terms</p>
              </div>
              <div className="text-center group hover:transform hover:scale-105 transition-transform duration-200">
                <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl w-16 h-16 flex items-center justify-center mx-auto mb-3 shadow-lg group-hover:shadow-xl transition-shadow">
                  <span className="text-white font-bold text-2xl">3</span>
                </div>
                <p className="font-semibold text-gray-900 mb-1 text-base">Get Rankings</p>
                <p className="text-sm">View instant results</p>
              </div>
            </div>
            
            {/* Stats Bar */}
            <div className="mt-6 pt-6 border-t-2 border-gray-200 grid grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-primary-600">50</p>
                <p className="text-xs text-gray-600 font-medium">Results Scanned</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-600">4</p>
                <p className="text-xs text-gray-600 font-medium">Keywords Max</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-indigo-600">∞</p>
                <p className="text-xs text-gray-600 font-medium">Searches</p>
              </div>
            </div>
          </div>
          
          <div className="mt-8 flex items-center justify-center gap-2 text-sm text-gray-600">
            
            <span className="text-gray-500">Powered by</span>
            <span className="font-semibold text-purple-600">Rank Spotter</span>
          </div>
          
          <p className="mt-3 text-xs text-gray-500">
            © 2025 Rank Spotter. Real-time Google ranking analysis.
          </p>
        </footer>
      </div>
    </div>
  );
}

export default App;
